import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAudioContext } from "@/shared/contexts/AudioContext";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { _sharedActionsLibrary } from "@/shared/store/library/actions";
import { selectorPlayerWaveformsDictionary } from "@/shared/store/player/selectors";
import {
  findWaveformsDictionaryParaById,
  findWaveformsDictionaryParaByPosition,
} from "@/shared/utils/player";

const useReaderSaveHistory = (
  activeId,
  saveML,
  activeChapterId,
  isInPlayerAnotherBook,
  isEnabled = true,
  isUseSingleEndPoint = false
) => {
  const dispatch = useDispatch();

  const refSaveHistoryParams = useRef({});

  const { getPlayerCurrentTime, chapterId: playerChapterId, isPlaying } = useAudioContext();

  const syncPlayerAndReader = useSelector(selectorSetting(SETTING.syncPlayerAndReader.id));
  const waveformsDictionary = useSelector(selectorPlayerWaveformsDictionary);

  const saveRead = (chapterId, paraId, saveML, delay = 0) => {
    refSaveHistoryParams.current = {
      lastReadChapterId: activeChapterId,
      lastReadId: activeId,
    };

    if (isUseSingleEndPoint) {
      const playingParaInformation = findWaveformsDictionaryParaById(
        waveformsDictionary,
        chapterId,
        paraId
      );
      const position = playingParaInformation?.start || 0;

      dispatch(_sharedActionsLibrary.saveAll(chapterId, paraId, position, saveML, delay));
      return;
    }

    dispatch(_sharedActionsLibrary.saveRead(chapterId, paraId, saveML, delay));
  };

  const saveListen = (chapterId, position, saveML, delay = 0) => {
    if (!chapterId) {
      return;
    }

    refSaveHistoryParams.current = {
      lastPlayedChapterId: chapterId,
    };

    if (isUseSingleEndPoint) {
      const playingParaInformation = findWaveformsDictionaryParaByPosition(
        waveformsDictionary,
        chapterId,
        position
      );

      dispatch(
        _sharedActionsLibrary.saveAll(
          chapterId,
          playingParaInformation?.id || chapterId,
          position,
          saveML,
          delay
        )
      );
      return;
    }

    dispatch(_sharedActionsLibrary.saveListen(chapterId, position, saveML, delay));
  };

  // read checkpoint
  useEffect(() => {
    if (
      // Listen priority over Reading when sync Reader & Player.
      (isPlaying && playerChapterId && syncPlayerAndReader && !isInPlayerAnotherBook) ||
      !activeChapterId ||
      !isEnabled
    ) {
      return;
    }

    saveRead(activeChapterId, activeId, saveML, 1000);
  }, [activeChapterId, activeId, saveML, isEnabled]);

  // listen checkpoint
  useEffect(() => {
    if (!playerChapterId || isInPlayerAnotherBook || !isPlaying || !isEnabled, isPlaying) {
      return;
    }

    saveListen(playerChapterId, getPlayerCurrentTime(), saveML, 1000);
  }, [isEnabled, syncPlayerAndReader, playerChapterId, isInPlayerAnotherBook, isPlaying]);

  // unmount
  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    return () => {
      const { lastReadChapterId, lastReadId, lastPlayedChapterId } = refSaveHistoryParams.current;

      if (isUseSingleEndPoint) {
        let position, waveforms;
        if (lastPlayedChapterId) {
          position = getPlayerCurrentTime();
          waveforms = findWaveformsDictionaryParaByPosition(
            waveformsDictionary,
            lastPlayedChapterId,
            position
          );
        } else {
          waveforms = findWaveformsDictionaryParaById(
            waveformsDictionary,
            lastPlayedChapterId,
            lastReadId
          );
          position = waveforms?.start || 0;
        }

        dispatch(
          _sharedActionsLibrary.saveAll(
            lastReadChapterId || lastPlayedChapterId,
            lastReadId || waveforms?.id || lastPlayedChapterId,
            position,
            saveML,
            0
          )
        );
        return;
      }

      if (lastPlayedChapterId) {
        dispatch(
          _sharedActionsLibrary.saveListen(lastPlayedChapterId, getPlayerCurrentTime(), saveML, 0)
        );
      } else if (lastReadChapterId) {
        dispatch(_sharedActionsLibrary.saveRead(lastReadChapterId, lastReadId, saveML, 0));
      }
    };
  }, [saveML, isEnabled, waveformsDictionary]);

  return {
    saveRead,
    saveListen,
    refSaveHistoryParams,
  };
};

export default useReaderSaveHistory;
